// Pure, deterministic Gin Rummy engine. Two hidden hands, atomic draw+discard turns, knock /
// gin / undercut scoring with layoffs, and play to 100 across hands. Arcade rotates turn
// ownership on every accepted move, so `hand.activePlayerId` flips on every move to stay in
// lockstep with the server. See docs/PRODUCT_AND_TECHNICAL_SPEC.md §5, §12.

import type { PlayerId } from "../game-core";
import { CardId, fullDeck, randomSeed, seededShuffle, sortCards } from "./cards";
import { bestMelds, layOff } from "./melds";

export const TARGET_SCORE = 100;
export const HAND_SIZE = 10;
export const KNOCK_LIMIT = 10;
export const GIN_BONUS = 25;
export const UNDERCUT_BONUS = 25;
/** Hand is a draw if the stock is down to this many cards after a discard. */
export const STOCK_FLOOR = 2;

export type DrawSource = "stock" | "discard";

export type GinMove =
  | { version: 1; type: "turn"; draw: DrawSource; discard: CardId }
  | { version: 1; type: "knock"; draw: DrawSource; discard: CardId }
  | { version: 1; type: "next_hand" }
  | { version: 1; type: "resign"; playerId?: PlayerId };

export type HandResultKind = "knock" | "gin" | "undercut" | "draw";

export interface HandResult {
  kind: HandResultKind;
  knockerId: PlayerId | null;
  scorerId: PlayerId | null;
  points: number;
  knockerDeadwood: number;
  opponentDeadwood: number;
  hands: Record<PlayerId, CardId[]>;
  melds: Record<PlayerId, CardId[][]>;
}

export interface GinHand {
  activePlayerId: PlayerId;
  dealerId: PlayerId;
  drawPile: CardId[];
  discardPile: CardId[];
  hands: Record<PlayerId, CardId[]>;
  turnNumber: number;
  result: HandResult | null;
}

export interface GinState {
  version: 1;
  rulesVersion: "gin-rummy/1";
  seed: string;
  match: {
    phase: "playing" | "complete" | "resigned";
    handNumber: number;
    targetScore: number;
    points: Record<PlayerId, number>;
    winnerId: PlayerId | null;
  };
  players: [PlayerId, PlayerId];
  hand: GinHand;
  revision: number;
  lastActionAt: string;
}

const other = (state: GinState, id: PlayerId): PlayerId =>
  state.players[0] === id ? state.players[1] : state.players[0];

/** Deal one hand deterministically. `activePlayerId` is set by the caller (server turn order). */
function dealHand(seed: string, handNumber: number, players: [PlayerId, PlayerId], activeId: PlayerId): GinHand {
  const cards = seededShuffle(fullDeck(), `${seed}:${handNumber}`);
  return {
    activePlayerId: activeId,
    dealerId: activeId === players[0] ? players[1] : players[0],
    hands: {
      [players[0]]: cards.slice(0, HAND_SIZE),
      [players[1]]: cards.slice(HAND_SIZE, HAND_SIZE * 2),
    },
    discardPile: [cards[HAND_SIZE * 2]],
    drawPile: cards.slice(HAND_SIZE * 2 + 1),
    turnNumber: 0,
    result: null,
  };
}

export function createInitialStateWithSeed(
  players: [PlayerId, PlayerId],
  seed: string,
  at: string = new Date().toISOString(),
): GinState {
  if (players[0] === players[1]) throw new Error("gin: players must be distinct");
  return {
    version: 1,
    rulesVersion: "gin-rummy/1",
    seed,
    match: {
      phase: "playing",
      handNumber: 1,
      targetScore: TARGET_SCORE,
      points: { [players[0]]: 0, [players[1]]: 0 },
      winnerId: null,
    },
    players,
    hand: dealHand(seed, 1, players, players[0]), // host (seat 0) acts first in hand 1
    revision: 0,
    lastActionAt: at,
  };
}

export const createInitialState = (players: [PlayerId, PlayerId], at?: string, seed?: string): GinState =>
  createInitialStateWithSeed(players, seed ?? randomSeed(), at);

export const activePlayer = (state: GinState): PlayerId | null =>
  state.match.phase === "playing" ? state.hand.activePlayerId : null;

export const isTerminal = (state: GinState): boolean =>
  state.match.phase === "complete" || state.match.phase === "resigned";

const topDiscard = (hand: GinHand): CardId => hand.discardPile[hand.discardPile.length - 1];

/** Apply a draw + discard to the active player's hand; returns the mutated piles/hands. */
function applyDrawDiscard(state: GinState, draw: DrawSource, discard: CardId) {
  const active = state.hand.activePlayerId;
  const taken = draw === "stock" ? state.hand.drawPile[0] : topDiscard(state.hand);
  const eleven = [...state.hand.hands[active], taken];
  const remaining = eleven.filter((c) => c !== discard);
  const drawPile = draw === "stock" ? state.hand.drawPile.slice(1) : state.hand.drawPile;
  const discardPile = (draw === "stock" ? state.hand.discardPile : state.hand.discardPile.slice(0, -1)).concat(discard);
  return {
    taken,
    hands: { ...state.hand.hands, [active]: remaining },
    drawPile,
    discardPile,
    activeHand: remaining,
  };
}

export function validateMove(state: GinState, move: GinMove, playerId: PlayerId): void {
  if (state.match.phase !== "playing") throw new Error("gin: match is not in progress");
  // Resignation is available to either player, including while waiting for the opponent's turn.
  if (move.type === "resign") {
    if (move.playerId !== undefined && move.playerId !== playerId) throw new Error("gin: invalid resigning player");
    return;
  }
  if (state.hand.activePlayerId !== playerId) throw new Error("gin: not your turn");

  if (move.type === "next_hand") {
    if (!state.hand.result) throw new Error("gin: the hand is not over yet");
    return;
  }

  // turn / knock share draw+discard legality.
  if (state.hand.result) throw new Error("gin: the hand is already resolved");
  if (move.draw === "stock" && state.hand.drawPile.length === 0) throw new Error("gin: the stock is empty");
  const taken = move.draw === "stock" ? state.hand.drawPile[0] : topDiscard(state.hand);
  if (move.draw === "discard" && move.discard === taken) throw new Error("gin: cannot take and discard the same card");
  const eleven = [...state.hand.hands[playerId], taken];
  if (!eleven.includes(move.discard)) throw new Error("gin: you do not hold that card");

  if (move.type === "knock") {
    const deadwood = bestMelds(eleven.filter((c) => c !== move.discard)).deadwoodValue;
    if (deadwood > KNOCK_LIMIT) throw new Error("gin: deadwood is too high to knock");
  }
}

function resolveKnock(state: GinState, knockerId: PlayerId, knockerHand: CardId[]): HandResult {
  const opponentId = other(state, knockerId);
  const opponentHand = state.hand.hands[opponentId];
  const knocker = bestMelds(knockerHand);
  const opponent = bestMelds(opponentHand);
  const revealHands = { [knockerId]: sortCards(knockerHand), [opponentId]: sortCards(opponentHand) };

  if (knocker.deadwoodValue === 0) {
    // Gin: no layoffs, 25-point bonus.
    return {
      kind: "gin",
      knockerId,
      scorerId: knockerId,
      points: opponent.deadwoodValue + GIN_BONUS,
      knockerDeadwood: 0,
      opponentDeadwood: opponent.deadwoodValue,
      hands: revealHands,
      melds: { [knockerId]: knocker.melds, [opponentId]: opponent.melds },
    };
  }

  const laid = layOff(opponent.deadwood, knocker.melds);
  const diff = laid.value - knocker.deadwoodValue;
  if (diff > 0) {
    return {
      kind: "knock",
      knockerId,
      scorerId: knockerId,
      points: diff,
      knockerDeadwood: knocker.deadwoodValue,
      opponentDeadwood: laid.value,
      hands: revealHands,
      melds: { [knockerId]: knocker.melds, [opponentId]: opponent.melds },
    };
  }
  // Undercut: defender ties or beats the knocker.
  return {
    kind: "undercut",
    knockerId,
    scorerId: opponentId,
    points: knocker.deadwoodValue - laid.value + UNDERCUT_BONUS,
    knockerDeadwood: knocker.deadwoodValue,
    opponentDeadwood: laid.value,
    hands: revealHands,
    melds: { [knockerId]: knocker.melds, [opponentId]: opponent.melds },
  };
}

function withScore(state: GinState, base: Partial<GinState>, result: HandResult): GinState {
  const points = { ...state.match.points };
  if (result.scorerId) points[result.scorerId] += result.points;
  const winnerId = result.scorerId && points[result.scorerId] >= state.match.targetScore ? result.scorerId : null;
  return {
    ...state,
    ...base,
    match: { ...state.match, points, winnerId, phase: winnerId ? "complete" : "playing" },
  } as GinState;
}

export function reduce(state: GinState, move: GinMove, at: string = new Date().toISOString()): GinState {
  validateMove(state, move, move.type === "resign" && move.playerId ? move.playerId : state.hand.activePlayerId);
  const active = state.hand.activePlayerId;
  const opponent = other(state, active);
  const base = { ...state, revision: state.revision + 1, lastActionAt: at };

  if (move.type === "resign") {
    const resigningPlayer = move.playerId ?? active;
    return { ...base, match: { ...state.match, phase: "resigned", winnerId: other(state, resigningPlayer) } };
  }

  if (move.type === "next_hand") {
    const handNumber = state.match.handNumber + 1;
    // Turn ownership rotates to `opponent` on this move, so they act first in the new hand.
    return {
      ...base,
      match: { ...state.match, handNumber },
      hand: dealHand(state.seed, handNumber, state.players, opponent),
    };
  }

  const { hands, drawPile, discardPile, activeHand } = applyDrawDiscard(state, move.draw, move.discard);
  const nextHandBase: GinHand = {
    ...state.hand,
    hands,
    drawPile,
    discardPile,
    activePlayerId: opponent,
    turnNumber: state.hand.turnNumber + 1,
  };

  if (move.type === "knock") {
    const result = resolveKnock({ ...state, hand: { ...state.hand, hands } }, active, activeHand);
    return withScore(state, { ...base, hand: { ...nextHandBase, result } }, result);
  }

  // Normal turn. If the stock is exhausted after the discard, the hand is a no-score draw.
  if (drawPile.length <= STOCK_FLOOR) {
    const result: HandResult = {
      kind: "draw",
      knockerId: null,
      scorerId: null,
      points: 0,
      knockerDeadwood: 0,
      opponentDeadwood: 0,
      hands: { [active]: sortCards(activeHand), [opponent]: sortCards(state.hand.hands[opponent]) },
      melds: {},
    };
    return { ...base, hand: { ...nextHandBase, result } };
  }

  return { ...base, hand: nextHandBase };
}

export function legalMoves(state: GinState, playerId: PlayerId): GinMove[] {
  if (state.match.phase !== "playing" || state.hand.activePlayerId !== playerId) return [];
  if (state.hand.result) return [{ version: 1, type: "next_hand" }];

  const moves: GinMove[] = [];
  const sources: DrawSource[] = state.hand.drawPile.length > 0 ? ["stock", "discard"] : ["discard"];
  for (const draw of sources) {
    const taken = draw === "stock" ? state.hand.drawPile[0] : topDiscard(state.hand);
    const eleven = [...state.hand.hands[playerId], taken];
    for (const discard of eleven) {
      if (draw === "discard" && discard === taken) continue;
      moves.push({ version: 1, type: "turn", draw, discard });
      if (bestMelds(eleven.filter((c) => c !== discard)).deadwoodValue <= KNOCK_LIMIT) {
        moves.push({ version: 1, type: "knock", draw, discard });
      }
    }
  }
  return moves;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

export function validateState(snapshot: unknown): GinState {
  if (!isRecord(snapshot)) throw new Error("gin: state is not an object");
  const s = snapshot as Record<string, unknown>;
  if (s.version !== 1 || s.rulesVersion !== "gin-rummy/1") throw new Error("gin: unsupported version");
  if (typeof s.seed !== "string") throw new Error("gin: missing seed");

  const players = s.players;
  if (!Array.isArray(players) || players.length !== 2 || typeof players[0] !== "string" || typeof players[1] !== "string" || players[0] === players[1]) {
    throw new Error("gin: invalid players");
  }
  const [p0, p1] = players as [string, string];

  const match = s.match;
  if (!isRecord(match) || (match.phase !== "playing" && match.phase !== "complete" && match.phase !== "resigned")) throw new Error("gin: invalid match");
  if (!isRecord(match.points) || typeof match.points[p0] !== "number" || typeof match.points[p1] !== "number") throw new Error("gin: invalid points");

  const hand = s.hand;
  if (!isRecord(hand) || !isRecord(hand.hands)) throw new Error("gin: invalid hand");
  const h0 = hand.hands[p0];
  const h1 = hand.hands[p1];
  const drawPile = hand.drawPile;
  const discardPile = hand.discardPile;
  if (!Array.isArray(h0) || !Array.isArray(h1) || !Array.isArray(drawPile) || !Array.isArray(discardPile)) throw new Error("gin: invalid piles");
  if (h0.length !== HAND_SIZE || h1.length !== HAND_SIZE) throw new Error("gin: hands must hold ten cards");
  if (hand.activePlayerId !== p0 && hand.activePlayerId !== p1) throw new Error("gin: invalid active player");

  // Deck conservation: the four zones together are exactly the 52-card deck.
  const all = [...h0, ...h1, ...drawPile, ...discardPile] as CardId[];
  if (all.length !== 52 || new Set(all).size !== 52) throw new Error("gin: cards are not conserved");
  const deck = new Set(fullDeck());
  for (const card of all) if (!deck.has(card)) throw new Error("gin: unknown card");
  if (typeof s.revision !== "number" || typeof s.lastActionAt !== "string") throw new Error("gin: invalid metadata");

  return snapshot as unknown as GinState;
}
